// Toaster — глобальный поток уведомлений по DOM CustomEvent.
//
// Использование (откуда угодно):
//   import { showToast } from "./Toaster";
//   showToast({ kind: "warn", title: "Лимит", message: "10 / 10 слотов" });
//
// Toaster живёт один раз в корне App. Слушает window event "twap-toast"
// и накапливает toast'ы в стеке (max 3 видимых, остальные в очереди).
// Каждый toast автоматически уходит через `duration` мс.
//
// Стек располагается в верху по центру экрана, fade-in slide сверху.

import { useEffect, useRef, useState } from "react";

export type ToastKind = "info" | "warn" | "error" | "success";

export interface ToastInput {
  /** Stable key: повторный toast с тем же id обновляет существующий. */
  id?: string;
  kind?: ToastKind;
  title?: string;
  message: string;
  /** мс до auto-dismiss; 0 = не убирать сам. Default 4500. */
  duration?: number;
}

interface ToastEventDetail extends Required<Omit<ToastInput, "id" | "title">> {
  id?: string;
  title: string;
}

interface ToastItem extends Omit<ToastEventDetail, "id"> {
  id: number;
  toastKey?: string;
  version: number;
}

const EVENT_NAME = "twap-toast";

let _seq = 0;

/** Внешняя API: показать toast. Можно дёргать откуда угодно. */
export function showToast(input: ToastInput): void {
  const detail: ToastEventDetail = {
    id: input.id,
    kind: input.kind ?? "info",
    title: input.title ?? "",
    message: input.message,
    duration: input.duration ?? 4500,
  };
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
}

const COLORS: Record<ToastKind, { bg: string; border: string; text: string; icon: string }> = {
  info: {
    bg: "rgba(59, 130, 246, 0.12)",
    border: "rgba(59, 130, 246, 0.45)",
    text: "#93c5fd",
    icon: "ℹ",
  },
  warn: {
    bg: "rgba(251, 191, 36, 0.12)",
    border: "rgba(251, 191, 36, 0.50)",
    text: "#fcd34d",
    icon: "⚠",
  },
  error: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.50)",
    text: "#fca5a5",
    icon: "✕",
  },
  success: {
    bg: "rgba(34, 197, 94, 0.12)",
    border: "rgba(34, 197, 94, 0.50)",
    text: "#86efac",
    icon: "✓",
  },
};

const MAX_VISIBLE = 3;

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);
  // Храним handler в ref чтобы он не пересоздавался — добавляем listener один раз.
  const setItemsRef = useRef(setItems);
  const versionsRef = useRef<Map<number, number>>(new Map());
  setItemsRef.current = setItems;

  useEffect(() => {
    function onToast(e: Event) {
      const ce = e as CustomEvent<ToastEventDetail>;
      const d = ce.detail;
      let scheduledId = 0;
      let scheduledVersion = 0;
      setItemsRef.current((prev) => {
        const existing = d.id ? prev.find((i) => i.toastKey === d.id) : undefined;
        if (existing) {
          scheduledId = existing.id;
          scheduledVersion = existing.version + 1;
          versionsRef.current.set(scheduledId, scheduledVersion);
          return prev.map((i) =>
            i.id === scheduledId
              ? {
                  ...i,
                  toastKey: d.id,
                  kind: d.kind,
                  title: d.title,
                  message: d.message,
                  duration: d.duration,
                  version: scheduledVersion,
                }
              : i
          );
        }

        _seq += 1;
        scheduledId = _seq;
        scheduledVersion = 1;
        versionsRef.current.set(scheduledId, scheduledVersion);
        const item: ToastItem = {
          id: scheduledId,
          toastKey: d.id,
          kind: d.kind,
          title: d.title,
          message: d.message,
          duration: d.duration,
          version: scheduledVersion,
        };
        return [...prev, item].slice(-MAX_VISIBLE);
      });
      if (d.duration > 0) {
        window.setTimeout(() => {
          setItemsRef.current((prev) => {
            if (versionsRef.current.get(scheduledId) !== scheduledVersion) return prev;
            versionsRef.current.delete(scheduledId);
            return prev.filter((i) => i.id !== scheduledId);
          });
        }, d.duration);
      }
    }
    window.addEventListener(EVENT_NAME, onToast);
    return () => window.removeEventListener(EVENT_NAME, onToast);
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 18,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        alignItems: "center",
        pointerEvents: "none",
      }}
    >
      {items.map((it) => (
        <ToastCard key={it.id} item={it} onClose={() => setItems((prev) => prev.filter((p) => p.id !== it.id))} />
      ))}
    </div>
  );
}

function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const c = COLORS[item.kind];
  const [visible, setVisible] = useState(false);
  // Триггер entrance-анимации.
  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      role="alert"
      style={{
        pointerEvents: "auto",
        minWidth: "min(420px, 90vw)",
        maxWidth: "90vw",
        padding: "12px 16px",
        borderRadius: 10,
        background: c.bg,
        border: `1px solid ${c.border}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
        color: c.text,
        fontSize: 13,
        fontFamily: "inherit",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-10px)",
        transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
      }}
    >
      <span
        aria-hidden
        style={{
          fontSize: 16,
          lineHeight: 1.2,
          flexShrink: 0,
        }}
      >
        {c.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {item.title && (
          <div
            style={{
              fontWeight: 700,
              marginBottom: 2,
              color: "#fff",
            }}
          >
            {item.title}
          </div>
        )}
        <div style={{ lineHeight: 1.5 }}>{item.message}</div>
      </div>
      <button
        type="button"
        onClick={onClose}
        title="Закрыть"
        aria-label="Закрыть"
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          padding: 0,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 16,
          lineHeight: 1,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        ×
      </button>
    </div>
  );
}
